import clsx from 'clsx';
import {SanitySettings} from '../../../types';
import {useCookieStore} from '../../../hooks/useCookieStore';
import {Link} from '@shopify/hydrogen';
import {AnimatePresence, motion} from 'framer-motion';
import useHasHydrated from '../../../hooks/useHasHydrated';
import translate from '../../../utils/getTranslation';

type Props = {
  settings?: SanitySettings;
};

export default function CookiesModal({settings}: Props) {
  const {isOpen, handleClose} = useCookieStore();
  const hasHydrated = useHasHydrated();

  const TRANSITIONS = {
    DURATION: 0.4,
    EASE: [0.25, 0.1, 0.25, 1.0],
  };

  const VARIANTS = {
    open: {
      y: 0,
      originY: 0,
      opacity: 1,
      transition: {
        duration: TRANSITIONS.DURATION,
        ease: TRANSITIONS.EASE,
        delay: 1,
      },
    },
    closed: {
      y: `100%`,
      transition: {
        duration: TRANSITIONS.DURATION,
        ease: TRANSITIONS.EASE,
      },
    },
  };

  if (!hasHydrated) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={VARIANTS}
          initial="closed"
          animate="open"
          exit="closed"
          className={clsx('fixed bottom-0 right-0 z-[90] w-full')}
        >
          <div
            className={clsx(
              'items-center justify-between gap-4 rounded-sm bg-neutral-100/80 px-4 py-3 text-neutral-900 backdrop-blur-md md:flex md:px-8',
            )}
          >
            <div className="items-center gap-8 md:flex">
              <div className="mb-2 font-kormelink text-xl uppercase md:mb-0">
                Cookies
              </div>
              <div className="flex gap-1 text-sm">
                {settings?.cookies && (
                  <p>
                    {settings?.cookies}{' '}
                    {settings?.privacyLink.slug && (
                      <span>
                        <Link
                          className={clsx(
                            'opacity-50 transition-opacity duration-200',
                            'hover:opacity-100',
                          )}
                          to={settings?.privacyLink.slug}
                        >
                          {settings?.privacyLink.title}
                        </Link>
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <div>
              <button
                onClick={handleClose}
                className={clsx(
                  'w-full rounded-sm bg-neutral-100 py-2 px-3 text-center text-neutral-900',
                  'duration-150 ease-out hover:bg-neutral-200',
                  'mt-4 md:mt-0',
                )}
              >
                {translate('ACCEPT')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
